import { useCallback, useEffect, useState } from "react";
import { Loader2, Send, Eye, X, AlertTriangle, Stamp } from "lucide-react";
import { useEvent } from "@/context/AppContext";
import { api } from "@/lib/api";

export const isActive = (j) => j && ["queued", "processing"].includes(j.status);

export function useWatermarkJobs(convId) {
  const [jobs, setJobs] = useState([]);
  const load = useCallback(() => api.get("/watermark-jobs", { params: { conversation_id: convId } }).then((r) => setJobs(r.data)).catch(() => {}), [convId]);
  useEffect(() => { setJobs([]); load(); }, [load]);
  const upsert = (job) => setJobs((p) => (p.some((j) => j.id === job.id) ? p.map((j) => (j.id === job.id ? { ...j, ...job } : j)) : [...p, job]));
  useEvent("watermark.updated", (d) => d.job.conversation_id === convId && !d.job.purpose && upsert(d.job));
  const active = jobs.some(isActive);
  useEffect(() => { if (!active) return undefined; const t = setInterval(load, 5000); return () => clearInterval(t); }, [active, load]);
  const start = async (mediaId, params) => { const { data } = await api.post(`/vault/${mediaId}/watermark`, { conversation_id: convId, ...params }); upsert(data); return data; };
  const discard = async (id) => { await api.delete(`/watermark-jobs/${id}`).catch(() => {}); setJobs((p) => p.filter((j) => j.id !== id)); };
  const remove = (id) => setJobs((p) => p.filter((j) => j.id !== id));
  return { jobs, start, discard, remove };
}

function JobChip({ j, onOpen, onSend, onDiscard }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-[#232738] bg-[#12141D] px-2 py-1 text-[11px]" data-testid={`wm-job-${j.id}`}>
      <Stamp className="h-3.5 w-3.5 text-violet-400 shrink-0" />
      <span className="truncate max-w-[140px] text-slate-300" title={j.params?.text}>"{j.params?.text}"</span>
      {isActive(j) && <span className="flex items-center gap-1 text-amber-300" data-testid={`wm-job-progress-${j.id}`}><Loader2 className="h-3 w-3 animate-spin" />Elaborazione filigrana… <span className="num">{j.progress || 0}%</span></span>}
      {j.status === "ready" && <span className="font-bold text-emerald-400" data-testid={`wm-job-ready-${j.id}`}>Pronto per l'invio</span>}
      {j.status === "failed" && <span className="flex items-center gap-1 text-red-300" title={j.error}><AlertTriangle className="h-3 w-3" />Errore</span>}
      <button onClick={() => onOpen(j)} className="p-0.5 text-slate-400 hover:text-white" title="Apri" data-testid={`wm-job-open-${j.id}`}><Eye className="h-3.5 w-3.5" /></button>
      {j.status === "ready" && <button onClick={() => onSend(j)} className="p-0.5 text-emerald-400 hover:text-emerald-300" title="Invia" data-testid={`wm-job-send-${j.id}`}><Send className="h-3.5 w-3.5" /></button>}
      <button onClick={() => onDiscard(j.id)} className="p-0.5 text-slate-500 hover:text-red-400" title="Scarta" data-testid={`wm-job-discard-${j.id}`}><X className="h-3.5 w-3.5" /></button>
    </div>
  );
}

export function WatermarkJobsBar({ wm, onOpen, onSend }) {
  if (!wm.jobs.length) return null;
  return <div className="flex gap-2 mb-2 flex-wrap" data-testid="wm-jobs-bar">{wm.jobs.map((j) => <JobChip key={j.id} j={j} onOpen={onOpen} onSend={onSend} onDiscard={wm.discard} />)}</div>;
}
